import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import { ExamSimulator } from '../components/ExamSimulator';
import { StorageService } from '../services/storageService';
import { ScenarioChallenge, ExamSession } from '../types';
import { Loader2, Zap, ShieldCheck, Microscope, Database, Brain, Target, GraduationCap } from 'lucide-react';
import { ModuleHeader } from '../components/ModuleHeader';
import { PageLoader } from '../components/PageLoader';
import { AdUnit } from '../components/AdUnit';
import { useThemeTokens } from '../hooks/useActiveTheme';

interface PracticeExamProps {
  initialMode?: 'standard' | 'practice' | 'scenario' | 'advanced' | 'scenario_quiz';
}

export const PracticeExam: React.FC<PracticeExamProps> = ({ initialMode }) => {
  const tokens = useThemeTokens();
  const { challengeId, certId } = useParams<{ challengeId?: string, certId: string }>();
  const [challenge, setChallenge] = useState<ScenarioChallenge | null>(null);
  const [isLoading, setIsLoading] = useState(!!challengeId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (challengeId) {
      setIsLoading(true);
      setError(null);
      const targetCertId = certId || 'cna';
      
      StorageService.getScenarioChallenges(targetCertId, { id: challengeId })
        .then(data => {
            if (data && data.items && data.items.length > 0) {
                setChallenge(data.items[0]);
            } else {
                setError("Lesson or challenge not found.");
            }
        })
        .catch(err => {
            console.error("Scenario Ingestion Error:", err);
            setError("Could not load the practice test. Please try again.");
        })
        .finally(() => setIsLoading(false));
    }
  }, [challengeId, certId]);

  if (isLoading) {
    return (
      <PageLoader message="Loading Practice Test..." text="Ingesting Simulation Engine Data" />
    );
  }

  if (error) {
      return (
        <div className="max-w-2xl mx-auto py-32 text-center space-y-8 animate-pop">
            <div className="w-20 h-20 bg-rose-50 text-rose-500 rounded-[2rem] flex items-center justify-center mx-auto shadow-xl border border-rose-100">
                <Database size={40} />
            </div>
            <div className="space-y-2">
                <h2 className="text-2xl font-black text-slate-900 tracking-tight">Could Not Load test</h2>
                <p className="text-slate-500 font-medium">"{error}"</p>
            </div>
            <button onClick={() => window.history.back()} className="px-10 py-4 bg-slate-900 text-white rounded-2xl font-black text-[10px] tracking-widest shadow-xl">Go Back</button>
        </div>
      );
  }

  const sessionOverride: ExamSession | undefined = challenge ? {
    examConfig: {
      state: 'CHALLENGE',
      vendor: 'Registry Curated',
      timeLimitMinutes: 0, 
      passingPercent: 75,
      totalQuestions: challenge.questions?.length || 0,
      difficulty: challenge.difficulty
    },
    questions: challenge.questions || []
  } : undefined;

  return (
    <div className="animate-fade-in pb-12 max-w-7xl mx-auto">
      <ModuleHeader 
        icon={GraduationCap}
        title={challenge ? challenge.title : (initialMode === 'scenario_quiz' ? 'Sequential Scenario' : 'Simulation Lab')}
        description={challenge ? `"${challenge.description}"` : (initialMode === 'scenario_quiz' 
          ? 'Answer questions based on real-life patient stories.' 
          : 'Take a full practice test that looks just like the real exam. Use this to check your knowledge under a timer.')}
        badge={challenge ? `Challenge ${challengeId}` : "Practice Exam"}
        color="slate"
      />
      
      <div className="relative">
          <ExamSimulator initialMode={initialMode} sessionOverride={sessionOverride} />
      </div>
    </div>
  );
};